import React, { useEffect, useRef, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useQueryClient } from 'react-query';
import MessageInputEditButton from './MessageInputEditButton';
import { useBulletinMessages } from '../../../../context/bulletin-messages';
import Bin from '../../../icons/Bin';
import APIService from '../../../../services/api';
import { useBulletin, useBulletinResponse } from '../../../../context/bulletin';

export type MessageInputDataType = 'system' | 'category' | 'entry' | 'map';

interface MessageInputProps {
  label: string;
  id: string;
  placeholder?: string;
  defaultValue?: string | null;
  multiline?: boolean;
  type: MessageInputDataType;
}

function MessageInput({
  label,
  id,
  placeholder,
  defaultValue,
  multiline = false,
  type,
}: MessageInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [value, setValue] = useState(defaultValue ?? '');

  const { bulletinType, bulletinDate } = useBulletin();
  const bulletinResponse = useBulletinResponse();
  const { setHasErrors, setIsLoading, isLoading, hasErrors } =
    useBulletinMessages();
  const queryClient = useQueryClient();

  const submitting = isLoading?.[id] ?? false;
  const error = hasErrors?.[id] ?? false;

  useEffect(() => {
    if (canEdit) {
      inputRef.current?.focus();
    }
  }, [canEdit]);

  async function handleSubmit(v: string) {
    setCanEdit(false);
    setHasErrors(id, false);
    setIsLoading(id, true);
    try {
      if (bulletinType && bulletinDate) {
        await APIService.postBulletinMessage(
          bulletinType,
          bulletinDate,
          type,
          id,
          v,
          true,
        );
      } else {
        throw new Error('No bulletin date or type given');
      }
      if (v === '') {
        queryClient.invalidateQueries({
          queryKey: ['BulletinMessages', bulletinType, bulletinDate],
        });
        queryClient.invalidateQueries({
          queryKey: ['BulletinQuery', bulletinType, bulletinDate],
        });
      }
    } catch {
      setHasErrors(id, true);
    }
    setIsLoading(id, false);
  }

  return (
    <div
      style={{ flex: 1, display: 'flex', flexDirection: 'row', gap: 5 }}
      data-cy={`message-input-${id}`}
    >
      <Form style={{ flex: 1 }}>
        <Form.Group controlId={`floatingLabel-${label}`}>
          <Form.Label>{label}</Form.Label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {!bulletinResponse.bulletin.isValidated && (
              <MessageInputEditButton
                onClick={() => {
                  if (!canEdit) {
                    setCanEdit(true);
                  } else {
                    handleSubmit(value);
                  }
                }}
                canEdit={canEdit}
                loading={submitting}
                id={id}
              />
            )}
            <Form.Control
              ref={inputRef}
              type="text"
              as={multiline ? 'textarea' : 'input'}
              style={multiline ? { minHeight: 100 } : undefined}
              placeholder={placeholder}
              value={value}
              onChange={e => setValue(e.target.value)}
              disabled={
                !canEdit || submitting || bulletinResponse.bulletin.isValidated
              }
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  handleSubmit(value);
                }
              }}
              isInvalid={error}
              onBlur={() => {
                if (canEdit) {
                  handleSubmit(value);
                }
              }}
            />
            {!bulletinResponse.bulletin.isValidated && (
              <Button
                onClick={() => handleSubmit('')}
                variant="danger"
                disabled={submitting}
                data-cy={`message-input-delete-button-${id}`}
              >
                {submitting ? (
                  <Spinner animation="border" role="status" size="sm">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                ) : (
                  <Bin fill="white" />
                )}
              </Button>
            )}
          </div>
          {error && (
            <Form.Text className="text-danger">
              An error occured, please try again.
            </Form.Text>
          )}
        </Form.Group>
      </Form>
    </div>
  );
}

export default MessageInput;
